import {
  FairlyPrioritizedUsers,
  Flexible,
  Meeting,
  Strict,
} from '@chilipiper/api-client/src/distribution'
import {
  PublicGroupSchedulingLink,
  PublicAdminSchedulingLink,
  AlwaysInvitedUser,
  PublicOwnershipSchedulingLinkV2,
  PublicRoundRobinSchedulingLinkV2,
  OwnershipSettingsV4,
  PublicOwnershipSchedulingLinkV2OwnershipHost,
  PublicOwnershipSchedulingLinkV2BookerInvitation,
  ArrayAlwaysInvitedUser,
  PublicOwnershipSchedulingLinkV2Assignment,
  PublicOwnershipSchedulingLinkV2DistributionHost,
  OwnershipRoutingResponse,
} from '@chilipiper/api-client/src/team-scheduling'
import {
  PublicRoutingInfoForSelected,
  PublicRoutingInfoForScheduled,
  SanitizedAssigneeSelection,
  PrimaryGuest,
  ExpectedHost,
} from '@chilipiper/api-client/src/handoff'
import { PublicMeetingType } from '@chilipiper/api-client/src/meeting-types'
import {
  GroupSchedulingLink2,
  ProductFeatureV3,
} from '@chilipiper/api-client/src/user-availability'

import {
  BookerInvitation,
  DistributionAssignmentsAttendance,
  DistributionAssignmentsAttendanceMap,
} from '../../types'

export type DistributionUsers = {
  id: string
} & FairlyPrioritizedUsers

export type StrictDistribution = FairlyPrioritizedUsers & {
  assignmentTypeConfig: Meeting & {
    handling: Strict
  }
}

export type FlexibleDistribution = FairlyPrioritizedUsers & {
  assignmentTypeConfig: Meeting & {
    handling: Flexible
  }
}
export type WithDistribution = {
  distributions: DistributionUsers[]
  attendance: DistributionAssignmentsAttendanceMap
}

export const isStrictDistribution = (
  distribution?: FairlyPrioritizedUsers
): distribution is StrictDistribution => {
  return (
    distribution?.assignmentTypeConfig.type === 'Meeting' &&
    distribution?.assignmentTypeConfig.handling.type === 'Strict'
  )
}

export const isFlexibleDistribution = (
  distribution?: FairlyPrioritizedUsers
): distribution is FlexibleDistribution => {
  return (
    distribution?.assignmentTypeConfig.type === 'Meeting' &&
    distribution?.assignmentTypeConfig.handling.type === 'Flexible'
  )
}

export type Owner = {
  ownerId: string
  teamId: string
  ruleId: string
}

export type MixedResult = DistributionUsers | Owner

export type MixedResults = {
  result: MixedResult[]
  attendance: Map<string, 'Required' | 'Optional'>
}

export type OwnerFromSimpleRule = {
  ownerId: string
  teamId: string
  attendance: 'Required' | 'Optional'
}

export const isWithDistribution = (
  result: OwnershipLinkRoutingResult
): result is WithDistribution => {
  return 'distributions' in result
}

export const isMixedResults = (result: OwnershipLinkRoutingResult): result is MixedResults => {
  return 'result' in result
}

export const isOwnerFromSimpleRule = (
  result: OwnershipLinkRoutingResult
): result is OwnerFromSimpleRule => {
  return 'ownerId' in result && 'teamId' in result && !('ruleId' in result)
}

export const isDistributionUsers = (item: MixedResult): item is DistributionUsers => {
  return 'id' in item && 'users' in item
}

export const isOwner = (item: MixedResult): item is Owner => {
  return 'ownerId' in item && 'teamId' in item && 'ruleId' in item
}

export const isGroupSchedulingLinkProductFeature = (
  productFeature?: ProductFeatureV3
): productFeature is GroupSchedulingLink2 => {
  return productFeature?.type === 'GroupSchedulingLink'
}

export type RoundRobinLinkRoutingResult = WithDistribution

export type OwnershipLinkRoutingResult = WithDistribution | MixedResults | OwnerFromSimpleRule

export type RoundRobinLinkDistributionRoute = {
  type: 'RoundRobinSchedulingLink'
  source: PublicRoundRobinSchedulingLinkV2
}

export type HandoffRouterDistributionRoute = {
  type: 'HandoffRouter'
  source: PublicRoutingInfoForSelected | PublicRoutingInfoForScheduled
}

export type OwnershipSchedulingLinkDistributionRoute = {
  type: 'OwnershipSchedulingLink'
  source: PublicOwnershipSchedulingLinkV2
}

export type GroupSchedulingLinkRoute = {
  type: 'GroupSchedulingLink'
  source: PublicGroupSchedulingLink
  hostId: string
}

export type AdminSchedulingLinkRoute = {
  type: 'AdminSchedulingLink'
  source: PublicAdminSchedulingLink
}

export type PersonalSchedulingLinkRoute = {
  type: 'PersonalSchedulingLink'
  source: PublicMeetingType
}

/**
 * @deprecated Use LinkResourceEnhanced instead. This type will be removed in a future version.
 *   Migrate to LinkResourceEnhanced which provides flattened properties for easier access.
 */
export type LinkResource =
  | RoundRobinLinkDistributionRoute
  | HandoffRouterDistributionRoute
  | OwnershipSchedulingLinkDistributionRoute
  | GroupSchedulingLinkRoute
  | AdminSchedulingLinkRoute
  | PersonalSchedulingLinkRoute

// ============================================================================
// Enhanced Link Resource Types
// ============================================================================

/** Base properties common to all link resources, flattened for easy access. */
interface BaseLinkResourceProps {
  /** Workspace identifier - present on all types */
  workspaceId?: string

  /** Meeting type IDs - normalized from various sources */
  meetingTypeIds: string[]

  /** Required attendees - from alwaysInvitedUsers or additionalInvitedUsers */
  requiredAttendees: AlwaysInvitedUser[]

  /** Booker settings - from meetingCreatedTarget (RoundRobin/Ownership only) */
  bookerInvitationSettings?: BookerInvitation
}

/** Link source containing the raw original data. Used internally by booking-flow helper functions. */
export interface LinkSource {
  /** Original source object - for internal use only */
  raw:
    | PublicRoundRobinSchedulingLinkV2
    | PublicOwnershipSchedulingLinkV2
    | PublicGroupSchedulingLink
    | PublicAdminSchedulingLink
    | PublicMeetingType
    | PublicRoutingInfoForSelected
    | PublicRoutingInfoForScheduled
}

/**
 * Enhanced LinkResource with flattened properties at root level for easier access.
 *
 * All common properties are accessible directly (e.g., linkResource.workspaceId) instead of
 * requiring source access (linkResource.source.workspaceId).
 */
export type LinkResourceEnhanced =
  | ({
      type: 'RoundRobinSchedulingLink'
      assignments: DistributionAssignmentsAttendance
      host: PublicRoundRobinSchedulingLinkV2['host']
      bookerInvitation: BookerInvitation
    } & BaseLinkResourceProps)
  | ({
      type: 'HandoffRouter'
      /** Selected assignees from HandoffRouter selectResponse */
      selectedAssignees?: SanitizedAssigneeSelection[]
      /** Selected meeting type ID from HandoffRouter selectResponse */
      selectedMeetingTypeId?: string
      /** Host information from HandoffRouter selectResponse */
      hostInfo?: ExpectedHost
      /** Primary guest from HandoffRouter */
      primaryGuest?: PrimaryGuest
    } & BaseLinkResourceProps)
  | ({
      type: 'OwnershipSchedulingLink'
      assignments?: PublicOwnershipSchedulingLinkV2Assignment[]
      ownershipSettings?: OwnershipSettingsV4
      host:
        | PublicOwnershipSchedulingLinkV2OwnershipHost
        | PublicOwnershipSchedulingLinkV2DistributionHost
      bookerInvitation: PublicOwnershipSchedulingLinkV2BookerInvitation
      alwaysInvitedUsers?: ArrayAlwaysInvitedUser
      routing?: OwnershipRoutingResponse
      linkId: string
    } & BaseLinkResourceProps)
  | ({
      type: 'GroupSchedulingLink'
      /** Optional attendees - from additionalInvitedUsers */
      optionalAttendees: AlwaysInvitedUser[]
      /** Host ID resolved from product feature or default host */
      hostId: string
      /** Whether the host is optional */
      isHostOptional: boolean
    } & BaseLinkResourceProps)
  | ({
      type: 'AdminSchedulingLink'
    } & BaseLinkResourceProps)
  | ({
      type: 'PersonalSchedulingLink'
    } & BaseLinkResourceProps)

/** Type guard to check if LinkResourceEnhanced is a GroupSchedulingLink. */
export const isGroupSchedulingLinkEnhanced = (
  linkResource?: LinkResourceEnhanced
): linkResource is Extract<LinkResourceEnhanced, { type: 'GroupSchedulingLink' }> => {
  return linkResource?.type === 'GroupSchedulingLink'
}
